import { generateText } from "@tiptap/core";
import { Link } from "@tiptap/extension-link";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { TextAlign } from "@tiptap/extension-text-align";
import { Underline } from "@tiptap/extension-underline";
import { generateHTML } from "@tiptap/html";
import { StarterKit } from "@tiptap/starter-kit";

import type { RichTextDoc, RichTextRootDoc } from "@miter/types/common";

export type { RichTextDoc } from "@miter/types/common";
// Keeps Link's setLink/unsetLink command typings in the emitted declarations for editor consumers.
export type { LinkOptions } from "@tiptap/extension-link";

/**
 * The single serialization schema for our rich text. Every place that turns stored JSON back
 * into HTML or React (dashboard, portal, mobile, PDF) must use this exact list, or nodes/marks
 * missing from the list are silently dropped. Editor-only extras (Placeholder, Markdown) live in
 * the editor and are irrelevant to (de)serialization, so they are not here.
 */
export const richTextExtensions = [
  StarterKit.configure({ heading: { levels: [1, 2, 3, 4] } }),
  Underline,
  Link.configure({ openOnClick: false, autolink: true }),
  TextAlign.configure({ types: ["heading", "paragraph"] }),
  TaskList,
  TaskItem.configure({ nested: true }),
];

/**
 * JSON -> HTML for consumers whose renderer can only take an HTML string (PDF/Puppeteer, mobile's
 * react-native-render-html). Uses the same extension list so the output matches the authoring schema.
 */
export const renderRichTextToHtml = (doc: RichTextDoc): string => generateHTML(doc, richTextExtensions);

/** Plain text -> doc. Each line becomes a paragraph; empty text nodes are invalid, so blank lines stay empty paragraphs. */
export const richTextFromPlainText = (text: string): RichTextDoc => ({
  type: "doc",
  content: text.split("\n").map((line) => ({
    type: "paragraph",
    ...(line ? { content: [{ type: "text", text: line }] } : {}),
  })),
});

/** Starting value for an empty editor. TipTap normalizes a bare doc to this on first render anyway. */
export const EMPTY_RICH_TEXT_DOC: RichTextDoc = { type: "doc", content: [{ type: "paragraph" }] };

/** Shape check for untrusted input at the API boundary; the schema parse happens downstream. */
export const isRichTextDoc = (val: unknown): val is RichTextRootDoc =>
  typeof val === "object" && val !== null && (val as { type?: unknown }).type === "doc";

const hasText = (node: RichTextDoc): boolean => {
  if (typeof node.text === "string" && node.text.trim()) return true;
  return (node.content ?? []).some(hasText);
};

export const richTextToPlainText = (doc: RichTextDoc): string =>
  generateText(doc, richTextExtensions, { blockSeparator: "\n" })
    .replace(/\n{2,}/g, "\n")
    .trim();

/** A doc is empty when it carries no non-whitespace text, matching the old tag-stripped check. */
export const isRichTextEmpty = (doc: RichTextDoc | null | undefined): boolean => !doc || !hasText(doc);
